import * as React from 'react'
import { walletContainerId, WALLET_LABELS } from '../useWalletPayment'
import { skeletonBlock } from './formParts'

// Google draws its button after its own readiness check, and not at all on a
// device that cannot pay: until a real button is inside, CSS keeps it hidden.
const DRAWS_LATE = new Set(['google_pay'])

function walletClass(method, ready) {
    if (!ready) return undefined
    return DRAWS_LATE.has(method)
        ? 'tw-mb-2 tw-min-h-[44px] [&:not(:has(button,iframe))]:tw-hidden'
        : 'tw-mb-2 tw-min-h-[44px]'
}

function WalletsSkeleton({ count }) {
    return (
        <div
            className="tw-flex tw-flex-col tw-gap-2"
            role="status"
            aria-label="Loading other ways to pay"
            data-testid="wallets-loading"
        >
            {Array.from({ length: count }, (_, index) => (
                <div key={index} className={skeletonBlock} />
            ))}
        </div>
    )
}

// `wallets` is the state of useWalletPayment. The buttons sit below Pay, so
// however late — or whether — they draw, nothing above the card fields moves.
// Their containers stay mounted while loading: Chargebee draws into them.
export default function WalletButtons({ wallets, disabled }) {
    if (!wallets.methods.length) return null
    const loading = !wallets.settled
    const anyReady = wallets.methods.some(method => wallets.ready[method])
    const anyDrawnReady = wallets.methods.some(
        method => wallets.ready[method] && !DRAWS_LATE.has(method)
    )
    return (
        <div className="tw-relative">
            {loading ? <WalletsSkeleton count={wallets.methods.length} /> : null}
            <div
                className={`cb-wallets tw-group tw-flex tw-flex-col${
                    loading ? ' tw-invisible tw-absolute tw-inset-x-0 tw-top-0' : ''
                }${disabled ? ' tw-pointer-events-none tw-opacity-50' : ''}`}
                aria-hidden={loading || undefined}
                aria-disabled={disabled || undefined}
                data-testid="payment-wallets"
            >
                {anyReady ? (
                    <div
                        className={`tw-mb-2 tw-items-center tw-gap-3 tw-text-xs tw-text-muted-foreground ${
                            anyDrawnReady
                                ? 'tw-flex'
                                : 'tw-hidden group-has-[button]:tw-flex group-has-[iframe]:tw-flex'
                        }`}
                    >
                        <span className="tw-h-px tw-flex-1 tw-bg-border" />
                        or pay with
                        <span className="tw-h-px tw-flex-1 tw-bg-border" />
                    </div>
                ) : null}
                {wallets.methods.map(method => (
                    <div
                        key={method}
                        id={walletContainerId(method)}
                        title={`Pay with ${WALLET_LABELS[method]}`}
                        data-testid={`wallet-${method}`}
                        // Spacing lives on the rendered buttons: a method still
                        // being set up needs its container but takes no room.
                        className={walletClass(method, wallets.ready[method])}
                    />
                ))}
            </div>
        </div>
    )
}
